import useHeader from '@hooks/useHeader';
import { useTranslation } from 'react-i18next';
import { useSuspenseQuery } from '@tanstack/react-query';
import EmailList from '../Components/EmailList';
import { useMemo } from 'react';
import { emailsDraftsQueryOptions } from '@utils/queryOptions';
import { useLoaderDeps, useNavigate } from '@tanstack/react-router';
import QuickOptions, { QuickOption } from '@Components/General/QuickOptions';
import { Box } from '@mui/material';
import useSimplePagination from '@hooks/useSimplePagination';
import useSimpleSort from '@hooks/useSimpleSort';
import Toolbar from '@Components/General/Toolbar';
import SimpleSort from '@Components/Common/SimpleSort';
import SimplePagination from '@Components/Common/SimplePagination';
import NoDrafts from './no-drafts';
import useEmptySearchParams from '@hooks/useEmptySearchParams';
import NoDraftsFound from './no-drafts-found';
import useEmailDraftOptions from './useEmailDraftOptions';

const EmailDrafts = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const searchParamsEmpty = useEmptySearchParams();

    const simplePageHook = useSimplePagination();
    const simpleSortHook = useSimpleSort({
        inverseFields: ['created', 'updated'],
        defaultSortField: '-updated',
    });

    const headerOptions = useMemo(() => {
        return {
            title: t('Email Drafts'),
            fabAction: () => () => navigate({ to: 'new' }),
            searchProps: {
                searchPlacholder: t('search:emails-drafts'),
            },
        };
    }, []);

    useHeader(headerOptions);

    const {
        data: { results, count },
    } = useSuspenseQuery(emailsDraftsQueryOptions(useLoaderDeps({})));

    const getDraftOptions = useEmailDraftOptions();

    const getQuickOptions = (item): QuickOption[] => {
        const options = getDraftOptions(item);
        return [options.duplicate, options.deleteEmail];
    };

    if (results.length === 0 && searchParamsEmpty) {
        return <NoDrafts />;
    }

    return (
        <Box flexGrow={1}>
            <Box display="flex" flexDirection="column" height="100%">
                <Toolbar>
                    <SimpleSort
                        sortOptions={[
                            { value: 'subject', label: t('Subject') },
                            { value: 'created', label: t('Last Created') },
                            { value: 'updated', label: t('Last Updated') },
                        ]}
                        {...simpleSortHook}
                    />
                    <SimplePagination count={count} {...simplePageHook} />
                </Toolbar>
                {results.length === 0 ? (
                    <NoDraftsFound />
                ) : (
                    <EmailList
                        data={results}
                        metaText={item =>
                            `${t('Edited')}: ${item.updated.toContextualDateTime()}`
                        }
                        secondaryPath="description"
                        action={item => (
                            <Box
                                onClick={e => e.preventDefault()}
                                onMouseDown={e => e.stopPropagation()}
                            >
                                <QuickOptions options={getQuickOptions(item)} />
                            </Box>
                        )}
                    />
                )}
            </Box>
        </Box>
    );
};

export default EmailDrafts;
